import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  Button
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

function EditSyntheticToken({ token, open, setOpen }) {
  const [form, setForm] = useState(token);
  const [status, setStatus] = useState(token?.status ?? false);
  const [logo, setLogo] = useState(token?.logo ?? '');

  useEffect(() => {
    setForm(token);
    setStatus(token?.status ?? false);
    setLogo(token?.logo ?? '');
  }, [token]);

  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleClose = () => setOpen(false);

  const saveToken = () => {
    axios
      .put(`${url}/crypto/updateToken`, {
        ...form,
        status
      })
      .then((res) => alert(JSON.stringify(res.data)))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('fileName', file.name);

    axios.post(`${url}/hootdex/token-logo-upload`, formData).then((res) => {
      if (res.data.status === 'ok') {
        const uploaded = `${url}/hootdex/images/${res.data.fileName}`;
        setLogo(uploaded);
        setForm({ ...form, logo: uploaded });
      }
    });
  };

  const normalizeToken = (token) => ({
    ...token,
    status: Boolean(token?.status),
    underlying_type_code: token?.underlying_type_code ?? '',
    pricing_type_code: token?.pricing_type_code ?? '',
    collateral_type_code: token?.collateral_type_code ?? '',
    collateralization_type_code: token?.collateralization_type_code ?? '',
    treasury_type_code: token?.treasury_type_code ?? '',
    treasury_key: token?.treasury_key ?? ''
  });

  useEffect(() => {
    if (!token) return;

    const normalized = normalizeToken(token);
    setForm(normalized);
    setStatus(normalized.status);
    setLogo(normalized.logo ?? '');
  }, [token, open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Box p={5} bgcolor="whitesmoke">
        {/* Logo */}
        <Box position="relative" mb={3}>
          <img src={logoWrapper(logo, token)} width={140} alt={form?.name} />
          <input hidden id="synthetic-logo" type="file" onChange={uploadLogo} />
          <label htmlFor="synthetic-logo">
            <Edit
              sx={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                cursor: 'pointer'
              }}
            />
          </label>
        </Box>

        {/* Status */}
        <Typography mb={2}>
          Status: {status ? 'Active' : 'Inactive'}
          <Switch
            checked={status}
            onChange={(e) => setStatus(e.target.checked)}
          />
        </Typography>

        {/* Identity */}
        <TextField
          fullWidth
          label="Token Name"
          margin="normal"
          value={form?.name ?? ''}
          onChange={handleChange('name')}
        />

        <TextField
          fullWidth
          label="Symbol"
          margin="normal"
          value={form?.symbol ?? ''}
          onChange={handleChange('symbol')}
        />

        <TextField
          fullWidth
          type="number"
          label="Total Supply"
          margin="normal"
          value={form?.amount ?? ''}
          onChange={handleChange('amount')}
        />

        {/* Underlying Asset */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="underlying-label">Underlying Asset</InputLabel>
          <Select
            labelId="underlying-label"
            label="Underlying Asset"
            value={form?.underlying_type_code ?? ''}
            onChange={handleChange('underlying_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="180101">BTC</MenuItem>
            <MenuItem value="180102">ETH</MenuItem>
            <MenuItem value="180103">SOL</MenuItem>
            <MenuItem value="180104">AVAX</MenuItem>
            <MenuItem value="180105">ADA</MenuItem>
            <MenuItem value="180106">DOGE</MenuItem>
            <MenuItem value="180107">BNB</MenuItem>
            <MenuItem value="180108">LTC</MenuItem>
            <MenuItem value="180109">ETC</MenuItem>
            <MenuItem value="180110">XRP</MenuItem>
          </Select>
        </FormControl>

        {/* Pricing */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="pricing-label">Pricing Method</InputLabel>
          <Select
            labelId="pricing-label"
            label="Pricing Method"
            value={form?.pricing_type_code ?? ''}
            onChange={handleChange('pricing_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="182101">Spot-Mirrored</MenuItem>
            <MenuItem value="182102">Spot + NAV</MenuItem>
            <MenuItem value="182103">Volatility Adjusted</MenuItem>
            <MenuItem value="182104">Liquidity Weighted</MenuItem>
            <MenuItem value="182105">Smart-Contract Oracle</MenuItem>
            <MenuItem value="182106">Multi-Oracle Median</MenuItem>
            <MenuItem value="182107">Forward Curve</MenuItem>
            <MenuItem value="182108">Basket Weighted</MenuItem>
          </Select>
        </FormControl>

        {/* Collateral Architecture */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="collateral-label">Collateral Architecture</InputLabel>
          <Select
            labelId="collateral-label"
            label="Collateral Architecture"
            value={form?.collateral_type_code ?? ''}
            onChange={handleChange('collateral_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="181101">Single Asset</MenuItem>
            <MenuItem value="181102">Multi Asset</MenuItem>
            <MenuItem value="181103">Stablecoin Backed</MenuItem>
            <MenuItem value="181104">Digital Only</MenuItem>
            <MenuItem value="181105">Hybrid RWA + Digital</MenuItem>
            <MenuItem value="181106">Smart-Contract Managed</MenuItem>
            <MenuItem value="181107">Issuer Managed</MenuItem>
          </Select>
        </FormControl>

        {/* Collateralization Level */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="collateralization-label">
            Collateralization Level
          </InputLabel>
          <Select
            labelId="collateralization-label"
            label="Collateralization Level"
            value={form?.collateralization_type_code ?? ''}
            onChange={handleChange('collateralization_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="183101">Fully Collateralized</MenuItem>
            <MenuItem value="183102">Over-Collateralized</MenuItem>
            <MenuItem value="183103">Partially Collateralized</MenuItem>
            <MenuItem value="183104">Dynamic</MenuItem>
            <MenuItem value="183105">Volatility Responsive</MenuItem>
          </Select>
        </FormControl>

        {/* Treasury Composition */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="treasury-label">Treasury Composition</InputLabel>
          <Select
            labelId="treasury-label"
            label="Treasury Composition"
            value={form?.treasury_type_code ?? ''}
            onChange={handleChange('treasury_type_code')}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="187101">BTC Heavy</MenuItem>
            <MenuItem value="187102">ETH Heavy</MenuItem>
            <MenuItem value="187103">SOL Heavy</MenuItem>
            <MenuItem value="187104">PECU Heavy</MenuItem>
            <MenuItem value="187105">USXM Heavy</MenuItem>
            <MenuItem value="187106">USDT Heavy</MenuItem>
            <MenuItem value="187107">Balanced</MenuItem>
            <MenuItem value="187108">Volatility Hedged</MenuItem>
            <MenuItem value="187109">Yield Optimized</MenuItem>
          </Select>
        </FormControl>

        <TreasuryLockStatusFields tokenType="SYNTHETICS" tokenId={form?.id} />

        {/* Description */}
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Description"
          margin="normal"
          value={form?.description ?? ''}
          onChange={handleChange('description')}
        />

        <Button
          fullWidth
          sx={{ mt: 4, bgcolor: 'darkgreen', color: 'white' }}
          onClick={saveToken}
        >
          Save Synthetic Token
        </Button>
      </Box>
    </Dialog>
  );
}

export default React.memo(EditSyntheticToken);
